/* Copyright (C) Okahu Inc 2023-2024. All rights reserved. */

'use client';

import { useEffect, useRef, useState } from 'react';
import { useLanguage } from '@/providers/LanguageProvider';
import { actionTypes } from '@/reducers/actionTypes/actionTypes';
import { handleError } from '@/utils/handelErrorMessage';
import { zodResolver } from '@hookform/resolvers/zod';
import componentsArr from 'data/components-library.json';
import { ChevronDown } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import {
  AssociableApps,
  Component,
  ComponentJSON,
  ComponentsData,
  ComponentTypeType,
  StateType,
} from 'types/api-data';
import { OptionsType, OptionsTypes } from 'types/multi-select';
import { IDependencies, Service } from 'types/services';
import { z } from 'zod';

import {
  createNameWithSuffix,
  removeFilterDuplicates,
  serviceDropdownDataTransform,
} from '@/lib/helper';
import { cn } from '@/lib/utils';
import { useComponents } from '@/hooks/useComponents';
import { useExperienceScopes } from '@/hooks/useExperienceScope';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

import { Input } from '../../ux-common/src/components/ui/input';
import { Logo } from '../cards';
import { EnhancedMultiSelect } from '../combobox';
import { CustomButton } from '../common';
import { Icons } from '../icons';
import { NotFoundMessage } from '../not-found';
import { FullScreenSpinner } from '../spinner/full-screen-spinner';
import Loader from '../spinner/loader';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '../ui/form';
import { ScrollArea } from '../ui/scroll-area';
import { Separator } from '../ui/separator';
import { DataDropdown } from './data-dropdown';

export interface DropdownDataType {
  name: string;
  id: string;
}

export const ConfigureComponentCard = ({
  type,
  name,
  desc,
  openDialog,
  setOpenDialog,
  isNew,
  isEdit,
  component,
  workflowName,
}: {
  type?: string;
  name?: string;
  desc?: string;
  openDialog: boolean;
  setOpenDialog: (value: boolean) => void;
  isNew?: boolean;
  isEdit?: boolean;
  component?: Component;
  workflowName?: string;
}) => {
  const [appsLoading, setAppsLoading] = useState(false);
  const [frameworkLoading, setFrameworkLoading] = useState(false);
  const [workflowsLoading, setWorkflowsLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [updatingComponent, setUpdatingComponent] = useState(false);
  const [selectedApps, setSelectedApps] = useState<AssociableApps[]>([]);
  const [selectedWorkflows, setSelectedWorkflows] = useState<ComponentsData>(
    []
  );
  const [selectedFrameworks, setSelectedFrameworks] = useState<OptionsType[]>(
    []
  );
  const [selectedService, setSelectedService] =
    useState<DropdownDataType | null>(null);
  const [componentTypeSelected, setComponentTypeSelected] =
    useState<DropdownDataType | null>(null);
  const [modelTypeSelected, setModelTypeSelected] =
    useState<DropdownDataType | null>(null);
  const [agentTypeSelected, setAgentTypeSelected] =
    useState<DropdownDataType | null>(null);
  const [toolTypeSelected, setToolTypeSelected] =
    useState<DropdownDataType | null>(null);
  const [serviceLoading, setServiceLoading] = useState(false);
  // Initialize experience scopes hook
  const experienceScopes = useExperienceScopes(
    component,
    type,
    componentTypeSelected,
    componentsArr
  );
  const isBuiltIn = !isNew;

  // Track initial values for custom dirty state detection
  const initialValues = useRef({
    frameworks: [] as OptionsType[],
    modelType: null as DropdownDataType | null,
    service: null as DropdownDataType | null,
    apps: [] as AssociableApps[],
    workflows: [] as ComponentsData,
    displayName: '',
  });
  const initialValuesSet = useRef(false);

  const { messages } = useLanguage();
  const langData = messages?.General;
  const componentData = messages?.ComponentsPage;

  const {
    dispatch,
    state: {
      activeServices,
      associableApps,
      frameworks,
      serviceDependencies,
      activeWorkflows,
    },
    componentsActions,
  } = useComponents();

  const FormSchema = z.object({
    display_name: z.string().min(2, {
      message: langData?.name_validation,
    }),
  });

  const form = useForm<z.infer<typeof FormSchema>>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      display_name: component?.display_name || '',
    },
  });

  const { formState, reset } = form;
  const { isDirty, isSubmitted, isSubmitting } = formState;

  // Helper function to safely compare arrays
  const arraysEqual = (
    arr1: any[],
    arr2: any[],
    keyExtractor?: (item: any) => any
  ) => {
    if (arr1.length !== arr2.length) return false;

    const getKey = keyExtractor || ((item) => item);
    const keys1 = arr1.map(getKey).sort();
    const keys2 = arr2.map(getKey).sort();

    return JSON.stringify(keys1) === JSON.stringify(keys2);
  };

  const getChangeStatus = () => {
    if (!isEdit) {
      return {
        formChanged: isDirty || !!componentTypeSelected,
        frameworksChanged: false,
        modelTypeChanged: false,
        serviceChanged: false,
        appsChanged: false,
        workflowChanged: false,
        anyChanged: isDirty || !!componentTypeSelected,
      };
    }

    if (!initialValuesSet.current) {
      return {
        formChanged: false,
        frameworksChanged: false,
        modelTypeChanged: false,
        serviceChanged: false,
        appsChanged: false,
        workflowChanged: false,
        anyChanged: false,
      };
    }

    const currentValues = initialValues.current;

    const formChanged = isDirty;
    const frameworksChanged = !arraysEqual(
      selectedFrameworks,
      currentValues.frameworks,
      (f) => f.value
    );
    const modelTypeChanged =
      (modelTypeSelected?.id || null) !== (currentValues.modelType?.id || null);
    const serviceChanged =
      (selectedService?.id || null) !== (currentValues.service?.id || null);
    const appsChanged = !arraysEqual(
      selectedApps,
      currentValues.apps,
      (a) => a.name
    );
    const workflowChanged = !arraysEqual(
      selectedWorkflows,
      currentValues.workflows,
      (a) => a.component_name
    );

    return {
      formChanged,
      frameworksChanged,
      modelTypeChanged,
      serviceChanged,
      workflowChanged,
      appsChanged,
      anyChanged:
        formChanged ||
        frameworksChanged ||
        modelTypeChanged ||
        serviceChanged ||
        appsChanged ||
        workflowChanged,
    };
  };

  // Function to set initial values
  const setInitialValues = () => {
    if (initialValuesSet.current) return;

    const findService = serviceDependencies.find(
      (item: any) => item.target_name
    );
    const matchedService =
      findService && activeServices
        ? serviceDropdownDataTransform(activeServices).find(
            (item) => item.id === findService.target_name
          )
        : null;

    const findApp = serviceDependencies.find((item: any) => item.source_name);
    const matchedApp =
      findApp && associableApps
        ? associableApps.find(
            (app: AssociableApps) => app.name === findApp.source_name
          )
        : null;

    const findWorkflow = serviceDependencies.find((item: any) => item.consumes);
    const matchedWorkflow =
      findWorkflow && activeWorkflows
        ? activeWorkflows.find(
            (workflow: Component) =>
              workflow.component_name === findWorkflow.source_name
          )
        : null;

    let matchedFrameworks: OptionsType[] = [];
    let matchedModelType: DropdownDataType | null = null;
    let matchedComponentType: DropdownDataType | null = null;

    if (component) {
      const type = component.type.split('.')[0];
      matchedComponentType =
        compTransformedDropdown.find((item) => item.id === type) || null;

      if (component.type.startsWith('model.')) {
        matchedModelType =
          modelTransformedDropdown.find((item) => item.id === component.type) ||
          null;
      }

      if (component.type.startsWith('workflow.')) {
        if (
          component.type !== 'workflow.generic' &&
          component.type !== 'workflow.hybrid'
        ) {
          matchedFrameworks = frameworkTransformedArr.find(
            (arr) => arr.value === component.type
          )
            ? [
                frameworkTransformedArr.find(
                  (arr) => arr.value === component.type
                )!,
              ]
            : [];
        }
      }

      if (component.properties?.included_workflow_types) {
        matchedFrameworks =
          (component.properties?.included_workflow_types
            ?.map((item) =>
              frameworkTransformedArr.find((arr) => arr.value === item)
            )
            .filter(Boolean) as OptionsType[]) || [];
      }
    }

    // Set initial values in ref
    initialValues.current = {
      frameworks: matchedFrameworks,
      modelType: matchedModelType,
      service: matchedService ?? null,
      workflows: matchedWorkflow ? [matchedWorkflow] : [],
      apps: matchedApp ? [matchedApp] : [],
      displayName: component?.display_name || '',
    };

    // Set state values
    setSelectedService(matchedService ?? null);
    setSelectedApps(matchedApp ? [matchedApp] : []);
    setSelectedWorkflows(matchedWorkflow ? [matchedWorkflow] : []);
    setSelectedFrameworks(matchedFrameworks);
    setModelTypeSelected(matchedModelType);
    setComponentTypeSelected(matchedComponentType);

    // Mark initial values as set
    initialValuesSet.current = true;
  };

  const matchData = (name: string) => {
    return activeServices.filter(
      (item: Service) => item.component_name === name
    )[0];
  };

  const fetchApps = async () => {
    try {
      setAppsLoading(true);
      await componentsActions.fetchAssociableApps();
    } catch (error) {
      console.log(error);
    } finally {
      setAppsLoading(false);
    }
  };

  const fetchFrameworks = async () => {
    try {
      setFrameworkLoading(true);
      await componentsActions.fetchFrameworks();
    } catch (error) {
      console.log(error);
    } finally {
      setFrameworkLoading(false);
    }
  };

  const fetchWorkflows = async () => {
    try {
      setWorkflowsLoading(true);
      await componentsActions.fetchActiveComponents();
    } catch (error) {
      console.log(error);
    } finally {
      setWorkflowsLoading(false);
    }
  };

  const handleAddApplication = (apps: string[]) => {
    const newlySelectedApp = associableApps.filter((app: AssociableApps) =>
      apps.includes(app.name)
    );

    setSelectedApps(newlySelectedApp);
  };

  const handleAddWorkflow = (workflows: any) => {
    const newlySelectedWorkflow = activeWorkflows.filter(
      (workflow: Component) => workflows.includes(workflow.component_name)
    );
    setSelectedWorkflows(newlySelectedWorkflow);
  };

  const handleFrameworkChange = (selectedValues: string[]) => {
    // Find the full objects from frameworkTransformedArr for the selected values
    const selected = frameworkTransformedArr.filter((item) =>
      selectedValues.includes(item.value)
    );
    setSelectedFrameworks(selected);
  };

  const transformedArray = associableApps
    ? associableApps?.map((item: AssociableApps, index: number) => ({
        id: index + 1,
        label: item.display_name,
        value: item.name,
      }))
    : [];

  const workflowsTransformedArr = activeWorkflows
    ?.filter((comp: Component) => comp.type.startsWith('workflow.'))
    .map((item: Component, id: number) => {
      return {
        id: id + 1,
        label: item.display_name,
        value: item.component_name,
      };
    });

  const frameworkTransformedArr: OptionsTypes = frameworks?.map(
    (framework: string, index: number) => {
      const match = componentsArr.components.find(
        (item) => item.type === framework
      );
      return {
        id: index + 1,
        label: match ? match.component_display_name : framework,
        value: framework,
        iconType: framework,
      };
    }
  );

  const inferFrameworkType = (): string | undefined => {
    if (selectedFrameworks.length >= 2) {
      return componentsArr.components.find(
        (item) => item.component_display_name === 'Hybrid Workflow'
      )?.type;
    } else if (selectedFrameworks.length === 1) {
      return selectedFrameworks[0].value;
    } else if (
      componentTypeSelected?.id === 'workflow' &&
      selectedFrameworks.length === 0
    ) {
      return componentsArr.components.find(
        (item) => item.component_display_name === 'Generic Workflow'
      )?.type;
    }
  };
  const inferModelType = (): string | undefined => {
    if (componentTypeSelected?.id === 'model' && !modelTypeSelected) {
      return componentsArr.components.find(
        (item) => item.component_display_name === 'Generic Model'
      )?.type;
    } else if (modelTypeSelected) {
      return modelTypeSelected.id;
    }
  };

  const inferAgentType = (): string | undefined => {
    if (
      'agent'.includes(componentTypeSelected?.name.toLowerCase() || '') &&
      !agentTypeSelected
    ) {
      return componentsArr.components.find(
        (item) => item.component_display_name === 'Generic Agent'
      )?.type;
    } else if (agentTypeSelected) {
      return agentTypeSelected.id;
    }
  };

  const inferToolType = (): string | undefined => {
    if (
      'agent'.includes(componentTypeSelected?.name.toLowerCase() || '') &&
      !toolTypeSelected
    ) {
      return componentsArr.components.find(
        (item) => item.component_display_name === 'Generic Tool'
      )?.type;
    } else if (toolTypeSelected) {
      return toolTypeSelected.id;
    }
  };

  const inferExperienceType = (): string | undefined => {
    if (experienceScopes.experienceTypeSelected) {
      return experienceScopes.experienceTypeSelected.id;
    }
    return componentTypeSelected?.id === 'experience'
      ? 'experience.custom'
      : undefined;
  };

  const inferType =
    inferExperienceType() ||
    inferModelType() ||
    inferFrameworkType() ||
    inferAgentType() ||
    inferToolType();

  const createComponent = async (data: any, component_name: string) => {
    try {
      setLoading(true);
      const payload = {
        ...data,
        type: type ? type : inferType,
        domain: 'logical',
        component_name: isEdit ? component?.component_name : component_name,
        status: 'active',
        properties: {
          included_workflow_types:
            selectedFrameworks.length >= 2
              ? selectedFrameworks.map((item) => item.value)
              : undefined,
          // handle scopes
          ...(experienceScopes.selectedExperienceTypes.length === 1
            ? {
                // For single type: use either predefined or custom scopes
                scopes: experienceScopes.selectedExperienceTypes.some(
                  (et) => et.id !== 'experience.custom'
                )
                  ? experienceScopes.selectedScopes // Predefined scopes
                  : experienceScopes.customScopes.reduce(
                      // Custom scopes
                      (acc, { name, value }) => {
                        if (name) acc[name] = value;
                        return acc;
                      },
                      {} as Record<string, string>
                    ),
              }
            : {}),
          // For multiple experience types: merge all scopes
          ...(experienceScopes.selectedExperienceTypes.length > 1
            ? {
                scopes: experienceScopes.mergeAllScopes(),
              }
            : {}),
        },
      };
      await componentsActions.createComponent(component_name, payload);
      reset({ display_name: '' });
      toast.success(componentData?.component_configured_success_text);
    } catch (error) {
      console.error('Error creating component:', error);
      handleError(error, componentData?.error_in_configuring_new_component);
    } finally {
      setLoading(false);
    }
  };

  const associateApps = async (component_name: string) => {
    try {
      setUpdating(true);
      const appDependencies: IDependencies[] = selectedApps.map((app) => ({
        source_name: app.name,
        dependency_type: 'comprises',
      }));
      let dependencies: IDependencies[] = [...appDependencies];
      // Add service dependency if selectedService exists
      if (selectedService) {
        const serviceDependency: IDependencies = {
          target_name: matchData(selectedService.id).component_name,
          dependency_type: 'served_by',
        };
        dependencies = [...dependencies, serviceDependency];
      }
      if (selectedWorkflows) {
        const workflowDependencies: IDependencies[] = selectedWorkflows.map(
          (workflow) => ({
            source_name: workflow.component_name,
            dependency_type: 'consumes',
          })
        );
        dependencies = [...dependencies, ...workflowDependencies];
      }
      const apps = selectedApps.map((app) => ({
        name: app.name,
        display_name: app.display_name,
      }));

      await componentsActions.updateComponentDependencies(
        component_name,
        dependencies
      );

      // Dispatch the associable apps and served_by service to the state
      dispatch({
        type: actionTypes.ASSOCIABLE_APPS,
        payload: {
          component_name,
          apps,
          served_by: selectedService
            ? {
                component_name: matchData(selectedService.id).componentName,
                display_name: matchData(selectedService.id).display_name,
                type: matchData(selectedService.id).type,
              }
            : undefined,
        },
      });
      toast.success(componentData?.dependencies_associated_successfully);
      setSelectedApps([]);
    } catch (error) {
      console.error('Error in associating dependencies:', error);
      handleError(error, componentData?.error_in_associating_dependencies);
    } finally {
      setUpdating(false);
    }
  };

  // Submit function for new workflow creation
  const newSubmit = async (
    data: z.infer<typeof FormSchema>,
    componentName: string
  ) => {
    await createComponent(data, componentName);
    if (selectedApps.length || selectedService || selectedWorkflows.length)
      await associateApps(componentName);
  };

  const updateSubmit = async (
    data: z.infer<typeof FormSchema>,
    componentName: string
  ) => {
    const payload = {
      ...data,
      type: type ? type : inferType,
      domain: 'logical',
      component_name: isEdit ? component?.component_name : componentName,
      status: 'active',
      properties: {
        included_workflow_types:
          selectedFrameworks.length >= 2
            ? selectedFrameworks.map((item) => item.value)
            : undefined,
        ...((type === 'experience.custom' ||
          inferType === 'experience.custom') && {
          scopes: experienceScopes.customScopes.reduce(
            (acc, { name, value }) => {
              if (name) acc[name] = value;
              return acc;
            },
            {} as Record<string, string>
          ),
        }),
      },
    };
    const { appsChanged, serviceChanged, formChanged, frameworksChanged } =
      getChangeStatus();
    if (appsChanged || serviceChanged) {
      await associateApps(componentName);
      await componentsActions.fetchComponents();
      await componentsActions.fetchWorkflowDependencies(workflowName);
    }
    if (formChanged || frameworksChanged) {
      try {
        setUpdatingComponent(true);
        await componentsActions.updateComponent(componentName, payload);
        await componentsActions.fetchComponents();
        toast.success(componentData?.component_updated_successfully);
      } catch (error) {
        console.error('Error in updating component:', error);
        handleError(error, componentData?.error_in_updating_component);
      } finally {
        setUpdatingComponent(false);
      }
    }
  };

  const onSubmit = async (data: z.infer<typeof FormSchema>) => {
    try {
      const component_name = createNameWithSuffix(data.display_name, 6);
      const componentName = isEdit ? component?.component_name : component_name;
      isEdit
        ? await updateSubmit(data, componentName!)
        : await newSubmit(data, componentName ?? component_name);
      setOpenDialog(false);
    } catch {
      setOpenDialog(false);
    }
  };

  const handleDelete = (app: AssociableApps) => {
    const newArr = selectedApps.filter(
      (selectedApp) => selectedApp.name !== app.name
    );
    setSelectedApps(newArr);
  };

  const handleDeleteWorkflow = (workflow: Component) => {
    const newArr = selectedWorkflows.filter(
      (selectedWorkflow) =>
        selectedWorkflow.component_name !== workflow.component_name
    );
    setSelectedWorkflows(newArr);
  };

  const handleDeleteFramework = (framework: OptionsType) => {
    const newFramework = selectedFrameworks.filter(
      (selectedFramework) => selectedFramework.label !== framework.label
    );
    setSelectedFrameworks(newFramework);
  };

  const handleClose = () => {
    setOpenDialog(!openDialog);
    reset();
    setSelectedApps([]);
    setSelectedWorkflows([]);
    setSelectedService(null);
    setSelectedFrameworks([]);
    setModelTypeSelected(null);
    setComponentTypeSelected(null);
    experienceScopes.setExperienceTypeSelected(null);
    // Reset the initial values flag
    initialValuesSet.current = false;
  };

  const spinners = [
    {
      condition: updating,
      text: componentData?.associating_dependencies,
    },
    {
      condition: loading,
      text: componentData?.creating_component,
    },
    {
      condition: updatingComponent,
      text: componentData?.updating_component,
    },
  ];

  const fetchActiveService = async () => {
    try {
      setServiceLoading(true);
      await componentsActions.fetchActiveServices();
    } catch (error) {
      console.log(error);
    } finally {
      setServiceLoading(false);
    }
  };

  // Transforming the data to match dropdown data and removing Agent as it is not supported
  const componentTypeDropdownDataTransform = (
    data: ComponentTypeType
  ): DropdownDataType[] => {
    return data.map((item) => ({
      id: item.type,
      name: item.type_display_name,
    }));
  };

  const modelTypeDropdownDataTransform = (
    data: ComponentJSON[]
  ): DropdownDataType[] => {
    return data.map((item) => ({
      id: item.type,
      name: item.component_display_name,
    }));
  };

  const supportedModels = componentsArr.components.filter(
    (item) => item.type.split('.')[0] === 'model' && item.state === 'supported'
  );

  const supportedAgents = componentsArr.components.filter(
    (item) => item.type.split('.')[0] === 'agent' && item.state === 'supported'
  );
  const supportedTool = componentsArr.components.filter(
    (item) => item.type.split('.')[0] === 'tool' && item.state === 'supported'
  );

  const compTransformedDropdown = componentTypeDropdownDataTransform(
    componentsArr.component_types
  );

  const modelTransformedDropdown = modelTypeDropdownDataTransform(
    supportedModels.map((item) => ({
      ...item,
      state: item.state as StateType,
    }))
  );

  const agentTransformedDropdown = supportedAgents.map((item) => ({
    id: item.type,
    name: item.component_display_name,
  }));
  const toolTransformedDropdown = supportedTool.map((item) => ({
    id: item.type,
    name: item.component_display_name,
  }));

  const isAgentOrTool =
    (type && ['agent', 'tool'].includes(type.split('.')[0])) ||
    ['agent', 'tool'].includes(componentTypeSelected?.name.toLowerCase() || '');

  useEffect(() => {
    if (isAgentOrTool) return;
    fetchApps();
    fetchActiveService();
    fetchFrameworks();
  }, []);

  useEffect(() => {
    if (type && ['agent', 'tool'].includes(type.split('.')[0])) {
      fetchWorkflows();
    }
  }, [type]);

  useEffect(() => {
    if (componentTypeSelected)
      if (
        ['agent', 'tool'].includes(componentTypeSelected?.name.toLowerCase())
      ) {
        fetchWorkflows();
      }
  }, [componentTypeSelected?.id]);

  useEffect(() => {
    if (!isEdit) {
      setSelectedFrameworks([]);
      setModelTypeSelected(null);
    }
  }, [componentTypeSelected]);

  // useEffect to handle setting initial values when all data is loaded
  useEffect(() => {
    if (
      isEdit &&
      component &&
      activeServices &&
      activeServices.length > 0 &&
      associableApps &&
      associableApps.length > 0 &&
      frameworks &&
      frameworks.length > 0 &&
      !initialValuesSet.current
    ) {
      setInitialValues();
    }
  }, [
    isEdit,
    component,
    activeServices,
    associableApps,
    frameworks,
    serviceDependencies,
  ]);

  return (
    <>
      {spinners.map(
        ({ condition, text }, index) =>
          condition && <FullScreenSpinner key={index} text={text as string} />
      )}
      <Dialog open={openDialog} onOpenChange={handleClose}>
        <DialogContent
          className="max-w-[800px] gap-6 bg-gradient-sky-dark"
          aria-describedby={undefined}
          onInteractOutside={(e) => e.preventDefault()}
        >
          <DialogHeader className="space-y-1">
            <DialogTitle className="text-2xl font-normal leading-7 text-body-primary">
              {isEdit ? langData?.edit_component : langData?.add_new_component}
            </DialogTitle>
            <DialogDescription className="text-form-input-text text-body-subtle">
              {langData?.dialog_description}
            </DialogDescription>
          </DialogHeader>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <ScrollArea className="h-[640px]">
                <div className="space-y-6">
                  {!isNew && (
                    <div className="space-y-1">
                      <p className="text-form-input-text text-form-input-rest">
                        {langData?.component}
                      </p>
                      <div className="rounded-lg border border-line-secondary bg-card-01 p-3">
                        <div className="flex items-center gap-x-3">
                          <Logo type={type!} />
                          <div className="flex flex-col">
                            <p className="text-h3 text-body-primary">{name}</p>
                            <p className="text-body text-body-subtle">{desc}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  <FormField
                    control={form.control}
                    name="display_name"
                    render={({ field }) => (
                      <FormItem className="space-y-1">
                        <FormLabel
                          htmlFor="name"
                          className="text-form-input-text font-normal text-form-input-rest"
                        >
                          {langData?.display_name}
                        </FormLabel>
                        <FormControl>
                          <Input
                            id="name"
                            className="rounded-sm border border-form-control-border-rest placeholder:text-form-input-text placeholder:text-form-input-disabled"
                            placeholder={langData?.instance_placeholder}
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  {isNew && (
                    <div className="flex flex-col gap-1">
                      <p className="text-form-input-text text-form-input-rest">
                        {langData?.component_type}{' '}
                      </p>
                      <DataDropdown
                        dropdownData={compTransformedDropdown}
                        loadingText={langData?.loading}
                        placeholderText={
                          langData?.choose_component_to_be_associated
                        }
                        dataSelected={componentTypeSelected}
                        handleSelect={(data) => setComponentTypeSelected(data)}
                        isDisabled={isEdit}
                        required={true}
                        showValidation={isSubmitted || isSubmitting}
                        errorMessage={langData?.required_field}
                      />
                    </div>
                  )}
                  <div>
                    {componentTypeSelected?.name.toLowerCase() === 'workflow' ||
                    type === 'workflow.hybrid' ? (
                      <div className="space-y-1">
                        <p className="flex items-center gap-1 text-form-input-text text-form-input-rest">
                          {messages?.ComponentsPage.framework_or_api}
                        </p>
                        <EnhancedMultiSelect
                          defaultValue={selectedFrameworks?.map(
                            (item) => item.value
                          )}
                          showPills={false}
                          loading={frameworkLoading}
                          options={frameworkTransformedArr}
                          onValueChange={handleFrameworkChange}
                          placeholder={
                            messages?.PromptFilters?.multi_select?.all
                          }
                          inputPlaceholder={langData?.choose_types_to_be_added}
                          hasDelete={true}
                        />
                      </div>
                    ) : componentTypeSelected?.name.toLowerCase() ===
                      'model' ? (
                      <div className="flex flex-col gap-1">
                        <p className="text-form-input-text text-form-input-rest">
                          {langData?.model_type}{' '}
                        </p>
                        <DataDropdown
                          dropdownData={modelTransformedDropdown}
                          loadingText={langData?.loading}
                          placeholderText={
                            langData?.choose_component_to_be_associated
                          }
                          dataSelected={modelTypeSelected}
                          handleSelect={(data) => setModelTypeSelected(data)}
                          isDisabled={isEdit}
                        />
                      </div>
                    ) : componentTypeSelected?.name.toLowerCase() ===
                      'agent' ? (
                      <div className="flex flex-col gap-1">
                        <p className="text-form-input-text text-form-input-rest">
                          {langData?.agent_type}{' '}
                        </p>
                        <DataDropdown
                          dropdownData={agentTransformedDropdown}
                          loadingText={langData?.loading}
                          placeholderText={
                            langData?.choose_type_to_be_associated
                          }
                          dataSelected={agentTypeSelected}
                          handleSelect={(data) => setAgentTypeSelected(data)}
                          isDisabled={isEdit}
                        />
                      </div>
                    ) : componentTypeSelected?.name.toLowerCase() === 'tool' ? (
                      <div className="flex flex-col gap-1">
                        <p className="text-form-input-text text-form-input-rest">
                          {langData?.tool_type}{' '}
                        </p>
                        <DataDropdown
                          dropdownData={toolTransformedDropdown}
                          loadingText={langData?.loading}
                          placeholderText={
                            langData?.choose_type_to_be_associated
                          }
                          dataSelected={toolTypeSelected}
                          handleSelect={(data) => setToolTypeSelected(data)}
                          isDisabled={isEdit}
                        />
                      </div>
                    ) : type?.startsWith('experience.') ||
                      componentTypeSelected?.name?.toLowerCase() ===
                        'experience' ? (
                      <>
                        {/* Experience Type Selector */}
                        <div className="mt-4 flex flex-col gap-1">
                          <p className="text-form-input-text text-form-input-rest">
                            {langData?.experience_type}
                          </p>

                          {isBuiltIn ? (
                            // Read-only view for built-in components
                            <div className="flex h-[42px] w-full items-center justify-between rounded-sm border border-form-control-border-rest">
                              <span className="ml-3 text-sm text-body-primary">
                                {experienceScopes.selectedExperienceTypes?.[0]
                                  ?.name || '—'}
                              </span>
                              <ChevronDown className="mx-2 size-4 text-form-input-rest opacity-50" />
                            </div>
                          ) : (
                            <EnhancedMultiSelect
                              defaultValue={experienceScopes.selectedExperienceTypes.map(
                                (item) => item.id
                              )}
                              showPills={false}
                              asChild={true}
                              options={componentsArr.components
                                .filter(
                                  (item) =>
                                    item.type.startsWith('experience.') &&
                                    item.state === 'supported'
                                )
                                .map((item, index) => ({
                                  id: index + 1,
                                  label: item.component_display_name,
                                  value: item.type,
                                }))}
                              onValueChange={(selected) => {
                                // Filter and map selected experience types
                                const selectedOptions = componentsArr.components
                                  .filter((item) =>
                                    item.type.startsWith('experience.')
                                  )
                                  .filter((item) =>
                                    selected.includes(item.type)
                                  )
                                  .map((item) => ({
                                    id: item.type,
                                    name: item.component_display_name,
                                  }));

                                // Update selected experience types
                                experienceScopes.setSelectedExperienceTypes(
                                  selectedOptions
                                );
                                // Clear all scopes if nothing selected
                                if (selected.length === 0) {
                                  experienceScopes.setAvailableScopes([]);
                                  experienceScopes.setPredefinedScopes({});
                                  experienceScopes.setSelectedScopes({});
                                  experienceScopes.setCustomScopes([]);
                                }
                                // Initialize empty custom scope if custom type selected
                                if (
                                  selected.includes('experience.custom') &&
                                  experienceScopes.customScopes.length === 0
                                ) {
                                  experienceScopes.setCustomScopes([
                                    { name: '', value: '' },
                                  ]);
                                }
                                if (selectedOptions.length === 1) {
                                  experienceScopes.setExperienceTypeSelected(
                                    selectedOptions[0]
                                  );
                                }
                              }}
                              placeholder={
                                messages?.PromptFilters?.multi_select?.all
                              }
                              inputPlaceholder={
                                langData?.choose_types_to_be_added
                              }
                              hasDelete={true}
                            >
                              <div className="flex h-[42px] w-full cursor-pointer items-center justify-between rounded-sm border border-form-control-border-rest">
                                <span className="ml-3 text-sm text-[#8C9EA1]">
                                  {experienceScopes.selectedExperienceTypes
                                    .length === 0
                                    ? langData?.choose_types_to_be_added
                                    : experienceScopes.selectedExperienceTypes
                                          .length === 1
                                      ? experienceScopes
                                          .selectedExperienceTypes[0].name
                                      : 'Custom Web/SMS'}
                                </span>
                                <ChevronDown className="mx-2 size-4 cursor-pointer text-form-input-rest" />
                              </div>
                            </EnhancedMultiSelect>
                          )}
                        </div>

                        {/* Facts Selection */}
                        {experienceScopes.selectedExperienceTypes.length >
                          0 && (
                          <>
                            {!isBuiltIn &&
                              experienceScopes.selectedExperienceTypes.length >
                                0 && (
                                <div className="mt-4 flex flex-col gap-1">
                                  <p className="flex items-center gap-1 text-form-input-text text-form-input-rest">
                                    {componentData?.facts}
                                    {/* Show count badge for predefined scopes */}
                                    {!experienceScopes.selectedExperienceTypes.every(
                                      (type) => type.id === 'experience.custom'
                                    ) && (
                                      <span className="flex h-6 w-fit items-center rounded-full bg-[#40565A] px-2 py-1 text-h5 leading-4 text-body-subtle">
                                        {Object.keys(
                                          experienceScopes.selectedScopes
                                        ).length || 0}
                                      </span>
                                    )}
                                  </p>
                                  {!experienceScopes.selectedExperienceTypes.every(
                                    (et) => et.id === 'experience.custom'
                                  ) && (
                                    <EnhancedMultiSelect
                                      defaultValue={Object.keys(
                                        experienceScopes.selectedScopes
                                      )}
                                      showPills={false}
                                      loading={false}
                                      options={experienceScopes.availableScopes.map(
                                        (scope, index) => ({
                                          id: index + 1,
                                          label: `${scope} (${experienceScopes.predefinedScopes[scope] || ''})`,
                                          value: scope,
                                        })
                                      )}
                                      onValueChange={(selected) => {
                                        const newScopes = selected.reduce(
                                          (acc, scope) => {
                                            acc[scope] =
                                              experienceScopes.predefinedScopes[
                                                scope
                                              ] || '';
                                            return acc;
                                          },
                                          {} as Record<string, string>
                                        );
                                        experienceScopes.setSelectedScopes(
                                          newScopes
                                        );
                                      }}
                                      placeholder={
                                        messages?.PromptFilters?.multi_select
                                          ?.all
                                      }
                                      inputPlaceholder="Choose the facts"
                                      hasDelete={true}
                                    />
                                  )}
                                </div>
                              )}

                            {/* Facts Display */}
                            <div className="mt-1 border border-line-secondary">
                              {experienceScopes.selectedExperienceTypes.map(
                                (type) => (
                                  <div key={type.id} className="mb-3">
                                    {type.id === 'experience.custom' ? (
                                      // Custom Experience Type
                                      <div className="p-3">
                                        <p className="mb-3 text-body text-form-input-disabled">
                                          {componentData?.custom_facts}
                                        </p>
                                        <div className="space-y-2">
                                          {experienceScopes.customScopes.map(
                                            (scope, index) => (
                                              <div
                                                key={index}
                                                className="flex items-center gap-2"
                                              >
                                                <Input
                                                  value={scope.name}
                                                  onChange={(e) => {
                                                    const updated = [
                                                      ...experienceScopes.customScopes,
                                                    ];
                                                    updated[index].name =
                                                      e.target.value;
                                                    experienceScopes.setCustomScopes(
                                                      updated
                                                    );
                                                  }}
                                                  placeholder="Display Name"
                                                />
                                                <Input
                                                  value={scope.value}
                                                  onChange={(e) => {
                                                    const updated = [
                                                      ...experienceScopes.customScopes,
                                                    ];
                                                    updated[index].value =
                                                      e.target.value;
                                                    experienceScopes.setCustomScopes(
                                                      updated
                                                    );
                                                  }}
                                                  placeholder="Scope Variable Name"
                                                />
                                                <CustomButton
                                                  className="bg-transparent px-0 hover:bg-transparent focus:bg-[#257C9D1F] active:bg-[#257C9D29] disabled:bg-transparent"
                                                  onClick={() =>
                                                    experienceScopes.setCustomScopes(
                                                      experienceScopes.customScopes.filter(
                                                        (_, i) => i !== index
                                                      )
                                                    )
                                                  }
                                                >
                                                  {Icons.TrashIcon(
                                                    'size-4 stroke-1'
                                                  )}
                                                </CustomButton>
                                              </div>
                                            )
                                          )}
                                          <CustomButton
                                            type="button"
                                            onClick={() =>
                                              experienceScopes.setCustomScopes([
                                                ...experienceScopes.customScopes,
                                                { name: '', value: '' },
                                              ])
                                            }
                                            className="gap-1 bg-transparent pl-2 pr-3 hover:bg-transparent focus:bg-[#257C9D1F] active:bg-[#257C9D29] disabled:bg-transparent"
                                          >
                                            {Icons.Plus('text-ocean size-4')}
                                            <span className="text-btn-label text-ocean">
                                              {
                                                messages?.ComponentsPage
                                                  .add_more
                                              }
                                            </span>
                                          </CustomButton>
                                        </div>
                                      </div>
                                    ) : (
                                      // Predefined Experience Type
                                      <>
                                        {experienceScopes.factsLoading ? (
                                          <div className="mt-1 flex h-[200px] items-center justify-center">
                                            <Loader />
                                          </div>
                                        ) : Object.keys(
                                            experienceScopes.selectedScopes
                                          ).length === 0 ? (
                                          <div className="mt-1 h-[200px]">
                                            <NotFoundMessage
                                              title="No facts associated"
                                              message="Select one or more facts to associate"
                                              outerDivClassName="gap-1 py-8 my-[0px]"
                                            />
                                          </div>
                                        ) : (
                                          <div className="mt-1 ">
                                            {Object.entries(
                                              experienceScopes.selectedScopes
                                            )
                                              .filter(
                                                ([scopeName]) =>
                                                  !experienceScopes.customScopes.some(
                                                    (custom) =>
                                                      custom.name === scopeName
                                                  )
                                              )
                                              .map(([scopeName, propName]) => (
                                                <div
                                                  key={scopeName}
                                                  className="flex items-center justify-between border-b border-b-line-secondary p-3"
                                                >
                                                  <span className="text-body">
                                                    {scopeName}
                                                    {propName && (
                                                      <span className="ml-2 text-form-input-text text-form-input-disabled">
                                                        {propName}
                                                      </span>
                                                    )}
                                                  </span>
                                                  {!isBuiltIn && (
                                                    <div className="flex items-center">
                                                      <CustomButton
                                                        className="flex items-center gap-2 bg-transparent hover:bg-transparent focus:bg-[#257C9D1F] active:bg-[#257C9D29] disabled:bg-transparent"
                                                        onClick={() =>
                                                          experienceScopes.handleMakeCustom(
                                                            scopeName,
                                                            propName
                                                          )
                                                        }
                                                      >
                                                        {Icons.SpannerIcon(
                                                          'size-4 text-[#257C9D] cursor-pointer'
                                                        )}
                                                        <span className="text-form-input-text font-medium text-ocean">
                                                          {
                                                            componentData?.make_custom
                                                          }
                                                        </span>
                                                      </CustomButton>
                                                      <Separator
                                                        orientation="vertical"
                                                        className="mx-3 h-full min-h-6 bg-line-tertiary"
                                                      />
                                                      <CustomButton
                                                        className='disabled:bg-transparent" bg-transparent px-0 hover:bg-transparent focus:bg-[#257C9D1F] active:bg-[#257C9D29]'
                                                        onClick={() =>
                                                          experienceScopes.handleDeleteScope(
                                                            scopeName
                                                          )
                                                        }
                                                      >
                                                        {Icons.TrashIcon(
                                                          'size-4 stroke-1 cursor-pointer'
                                                        )}
                                                      </CustomButton>
                                                    </div>
                                                  )}
                                                </div>
                                              ))}
                                          </div>
                                        )}
                                      </>
                                    )}
                                  </div>
                                )
                              )}
                            </div>
                          </>
                        )}
                      </>
                    ) : null}
                    {selectedFrameworks?.length > 0 ? (
                      <div
                        className={cn(
                          'mt-1 min-h-[200px] overflow-y-auto rounded-sm border border-form-control-border-rest'
                        )}
                      >
                        <ul className="list-none">
                          {selectedFrameworks?.map((framework) => (
                            <li
                              key={framework.id}
                              className="flex items-center justify-between border-b border-b-line-secondary p-3 text-sm"
                            >
                              <div className="flex items-center gap-x-2">
                                <Logo
                                  type={framework.value}
                                  size={{ img: 'size-6', bg: 'size-8' }}
                                />
                                <span>{framework.label}</span>
                              </div>
                              <button
                                onClick={() => handleDeleteFramework(framework)}
                              >
                                {Icons.TrashIcon(
                                  'size-5 stroke-1 cursor-pointer'
                                )}
                              </button>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ) : null}
                  </div>
                  {isAgentOrTool ? (
                    <div className="space-y-1">
                      <p className="flex items-center gap-1 text-form-input-text text-form-input-rest">
                        {messages?.ComponentsPage.workflow_associated}
                        <span className="flex h-6 w-fit items-center rounded-full bg-[#40565A] px-2 py-1 text-h5 leading-4 text-body-subtle">
                          {selectedWorkflows?.length || 0}
                        </span>
                      </p>
                      <EnhancedMultiSelect
                        defaultValue={selectedWorkflows?.map(
                          (item) => item.component_name
                        )}
                        showPills={false}
                        loading={workflowsLoading}
                        options={removeFilterDuplicates(
                          workflowsTransformedArr
                        )}
                        onValueChange={handleAddWorkflow}
                        placeholder={messages?.PromptFilters?.multi_select?.all}
                        inputPlaceholder={
                          messages?.ComponentsPage
                            ?.choose_workflows_to_be_associated
                        }
                        hasDelete={true}
                      />
                    </div>
                  ) : (
                    <>
                      {componentTypeSelected?.name.toLowerCase() !==
                        'experience' &&
                        !type?.startsWith('experience.') && (
                          <div className="flex flex-col gap-1">
                            <p className="text-form-input-text text-form-input-rest">
                              {langData?.service_associated}
                            </p>
                            <DataDropdown
                              dropdownData={serviceDropdownDataTransform(
                                activeServices
                              )}
                              loadingText={langData?.loading}
                              loading={serviceLoading}
                              placeholderText={
                                langData?.choose_services_to_be_associated
                              }
                              dataSelected={selectedService}
                              handleSelect={(data) => setSelectedService(data)}
                              showSearch={true}
                            />
                          </div>
                        )}
                      <div className="space-y-1">
                        <p className="flex items-center gap-1 text-form-input-text text-form-input-rest">
                          {messages?.ComponentsPage.application_associated}
                          <span className="flex h-6 w-fit items-center rounded-full bg-[#40565A] px-2 py-1 text-h5 leading-4 text-body-subtle">
                            {selectedApps?.length || 0}
                          </span>
                        </p>
                        <EnhancedMultiSelect
                          defaultValue={selectedApps?.map((item) => item.name)}
                          showPills={false}
                          loading={appsLoading}
                          options={removeFilterDuplicates(transformedArray)}
                          onValueChange={handleAddApplication}
                          placeholder={
                            messages?.PromptFilters?.multi_select?.all
                          }
                          inputPlaceholder={
                            messages?.ComponentsPage
                              ?.choose_applications_to_be_associated
                          }
                          hasDelete={true}
                        />
                      </div>
                    </>
                  )}
                </div>
                {isAgentOrTool ? (
                  <div
                    className={cn(
                      'mt-[3px] min-h-[200px] overflow-y-auto rounded-sm border',
                      selectedApps?.length > 0
                        ? 'border-form-control-border-rest'
                        : 'h-[200px] border-[#40565A]'
                    )}
                  >
                    {selectedWorkflows?.length > 0 ? (
                      <ul className="list-none">
                        {selectedWorkflows.map((workflow) => (
                          <li
                            key={workflow.component_name}
                            className="flex items-center justify-between border-b border-b-line-secondary p-3 text-sm"
                          >
                            <span>{workflow.display_name}</span>
                            <button
                              onClick={() => handleDeleteWorkflow(workflow)}
                            >
                              {Icons.TrashIcon(
                                'size-5 stroke-1 cursor-pointer'
                              )}
                            </button>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <NotFoundMessage
                        title={langData?.no_workflows_associated ?? ''}
                        message={langData?.no_workflows_associated_subtitle}
                        outerDivClassName="gap-1 py-8 my-[0px]"
                      />
                    )}
                  </div>
                ) : (
                  <div
                    className={cn(
                      'mt-[3px] min-h-[200px] overflow-y-auto rounded-sm border',
                      selectedApps?.length > 0
                        ? 'border-form-control-border-rest'
                        : 'h-[200px] border-[#40565A]'
                    )}
                  >
                    {selectedApps?.length > 0 ? (
                      <ul className="list-none">
                        {selectedApps.map((app) => (
                          <li
                            key={app.name}
                            className="flex items-center justify-between border-b border-b-line-secondary p-3 text-sm"
                          >
                            <span>{app.display_name}</span>
                            <button onClick={() => handleDelete(app)}>
                              {Icons.TrashIcon(
                                'size-5 stroke-1 cursor-pointer'
                              )}
                            </button>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <NotFoundMessage
                        title={langData?.no_applications_associated ?? ''}
                        message={langData?.no_applications_associated_subtitle}
                        outerDivClassName="gap-1 py-8 my-[0px]"
                      />
                    )}
                  </div>
                )}
              </ScrollArea>

              <Separator className="mt-4 bg-line-primary" />

              <DialogFooter className="mt-5 sm:justify-start">
                <CustomButton
                  type="submit"
                  customSize="regular"
                  customVariant="primary"
                  disabled={
                    (!isEdit && !isDirty) ||
                    !getChangeStatus().anyChanged ||
                    isSubmitting ||
                    (isNew && !componentTypeSelected) ||
                    experienceScopes.areFactsEmpty()
                  }
                >
                  {isEdit
                    ? isSubmitting
                      ? langData?.saving
                      : langData?.save
                    : isSubmitting
                      ? langData?.adding
                      : langData?.add}
                </CustomButton>
                <DialogClose asChild>
                  <CustomButton customSize="regular" customVariant="secondary">
                    {langData?.cancel}
                  </CustomButton>
                </DialogClose>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>
    </>
  );
};
